"use client"

/**
 * Shared payment-sheet shell for single-item settle flows (offer + auction).
 *
 * Owns: dialog/sheet shell, listing-meta fetch + shipping-method dropdown,
 * CartItemData synthesis, CheckoutForm + PayPalCheckout wiring, retry-settle
 * UI, merchant-not-ready gate. Stays inline in the scroll body — does NOT
 * reintroduce the floating-footer `paymentPortalTarget` portal that was
 * removed in `b1ae7bd4` (offers) and `12097730` (auctions). See
 * `.tondocs/ui/OVERLAY_FOOTER.md` and the documented feed/chat quick-checkout
 * exception (many payment rails do not fit in a floating footer).
 *
 * NOT owned: per-area gate evaluation (deadline-expired, FSM state, isBuyer,
 * isSettled). Each wrapper synthesises its own `gate` prop and renders its
 * own blocked-state copy. This keeps the discriminator on `variant.kind`
 * tight (offer vs auction) rather than fanning out into six error views.
 *
 * @see components/offers/offer-checkout-sheet.tsx — wrapper (offer surface)
 * @see components/detailed-listing-view/auction-winner-checkout-sheet.tsx — wrapper (auction surface)
 * @see lib/payments/checkout-meta.ts — listing-meta adapter
 * @see hex skill §3 (React) — domain decisions live in lib/, shell is delivery only
 */

import type { RawImageRef } from "@kraahl/contract"
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react"
import { AlertTriangle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { formatPrice } from "@/lib/utils"
import { useMobile } from "@/hooks/use-mobile"
import { CheckoutForm, isShippingFormComplete } from "@/components/cart/checkout-form"
import PayPalCheckout, { type SellerDetail } from "@/components/cart/paypal-checkout"
import { buildSellerDetail } from "@/lib/payments/seller-detail"
import { buildCheckoutIntent } from "@/lib/payments/build-checkout-intent"
import {
  buildCheckoutPayload,
  type CheckoutPayloadExternalShipping,
} from "@/lib/checkout-payload"
import type { ShippingFormData } from "@/components/cart/shipping-form"
import type { CartItemData } from "@/components/cart/types"
import { useCartCalculations } from "@/hooks/use-cart-calculations"
import { isCountryBannedForShipping as isPayPalCountryBanned } from "@/lib/paypal-countries"
import { SHEET_Z_NESTED } from "@/lib/sheet-styles"
import { fetchListingMeta, type CheckoutMeta } from "@/lib/payments/checkout-meta"

/**
 * Discriminator for the resolve-checkout-intent context. Each variant carries
 * the data the payment-sheet shell needs to synthesise a single-item
 * `CartItemData` and the right `OrderCreationContext` kind on settle.
 *
 * Switches on `kind.type` MUST end with `default: unreachable(x: never)`.
 */
export type PaymentSheetVariant =
  | {
      type: "offer"
      offerId: string
      userId: string | null
      sellerId: string
      currency: string
      item: {
        id: string
        listingId: string
        title: string
        /** Stored refs — flow into `CartItemData.images`, resolve at sinks. */
        images: RawImageRef[]
        sellerName: string
        sellerAvatarUrl?: RawImageRef
        amount: number
      }
    }
  | {
      type: "auction"
      listingId: string
      userId: string
      sellerId: string
      currency: string
      item: {
        id: string
        title: string
        description: string
        condition: string
        grade: string
        /** Stored refs — flow into `CartItemData.images`, resolve at sinks. */
        images: RawImageRef[]
        sellerName: string
        sellerAvatarUrl?: RawImageRef
        winningAmount: number
      }
    }

/**
 * Gate evaluated by the per-area wrapper BEFORE handing off to the shell. The
 * shell renders `blocked.body` verbatim (chrome-only) instead of the checkout
 * form. The "ready" case unlocks the meta fetch + checkout form.
 *
 * Why the wrapper evaluates: deadline / FSM / isBuyer / isSettled are
 * per-area domain decisions (offer-domain or auction-FSM); none should live
 * inside this shell. The wrapper is the right place — same reason the
 * existing files had switch-heavy `content` IIFEs.
 */
export type PaymentSheetGate =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "blocked"; body: ReactNode }

export interface PaymentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  variant: PaymentSheetVariant
  /**
   * Pre-evaluated per-area gate. When `blocked`, the shell skips meta fetch
   * and renders the supplied body. When `ready`, the shell takes over.
   * `loading` keeps the spinner while the wrapper is still resolving its
   * own state (e.g. fetching offer current-state).
   */
  gate: PaymentSheetGate
  /** Chrome title — visible on BOTH branches (`DockSheet` and `DialogCard`). */
  title: string
  /**
   * Per-area settle callback invoked after PayPal capture if the server did
   * not pre-settle. Returning `false` triggers the retry-settle UI with
   * `paypalData.orderID` as the pending order id.
   */
  onSettle: (orderId: string) => Promise<boolean>
  /**
   * Optional callback fired when the user dismisses the sheet from inside
   * the shell (close button / retry-skip). Wrappers usually just call
   * `onOpenChange(false)` here; provided as a hook in case the wrapper
   * needs to do extra cleanup (currently unused).
   */
  onClose?: () => void
  /**
   * Pre-existing pending order id (open-time existing-order check from the
   * wrapper — e.g. auction `/current-bid.existingOrderId`). When set, the
   * shell goes straight to retry-settle UI on open.
   */
  initialPendingOrderId?: string | null
  /**
   * Wrapper-side label overrides for the retry-settle UI. Defaults match the
   * auction copy verbatim — offer wrapper overrides to its translated keys.
   */
  retryCopy?: {
    title: string
    description: string
    retryLabel: string
    retryingLabel: string
    closeLabel: string
  }
  /**
   * Wrapper-side label overrides for the merchant-not-ready gate. Optional;
   * defaults match the previous auction copy verbatim.
   */
  merchantCopy?: {
    title: string
    description: string
    closeLabel: string
    /** Detailed merchantError string (e.g. translated message). */
    detail?: string
  }
  /** Shipping-method dropdown label (i18n via wrapper). */
  shippingLabel?: string
}

type RetryCopy = NonNullable<PaymentSheetProps["retryCopy"]>
type MerchantCopy = NonNullable<PaymentSheetProps["merchantCopy"]>

const DEFAULT_RETRY_COPY: RetryCopy = {
  title: "Payment received",
  description:
    "Your payment was captured, but we couldn't finalize the settlement. Please retry or contact support.",
  retryLabel: "Retry Settlement",
  retryingLabel: "Retrying…",
  closeLabel: "Close",
}

const DEFAULT_MERCHANT_COPY: MerchantCopy = {
  title: "Payment not available",
  description:
    "The seller's payment account is not ready to receive payments. Please contact the seller or try again later.",
  closeLabel: "Close",
}

/**
 * Synthesise a single-item `CartItemData` for the PaymentSheet shell from a
 * `PaymentSheetVariant` + the fetched `CheckoutMeta`. Exported for test pins
 * (WS4.7 §6.1 C8/C10) — production callers should NOT import this; they get
 * the same shape via the internal `scopedItems` memo. The export carries
 * because the live-rate gate (hooks/use-cart-calculations.ts:41-43) reads
 * `i.shippingMode === "live_rate"` to bypass the "method required" check; a
 * regression that drops `shippingMode` propagation here would silently
 * re-disable the Pay button on offer/auction live_rate surfaces.
 *
 * @internal — UI shell helper, not a stable API.
 */
export function variantToCartItem(variant: PaymentSheetVariant, meta: CheckoutMeta, selectedShippingId: string | null): CartItemData {
  const selected =
    meta.shippingMethods.find((m) => m.id === selectedShippingId) ??
    meta.shippingMethods[0] ??
    null

  // WS4.7 Phase 4.5 — propagate live-rate routing surface from CheckoutMeta
  // onto the synthesised CartItemData. The downstream gate at
  // hooks/use-cart-calculations.ts:41-43 reads `i.shippingMode === "live_rate"`
  // to bypass the "selectedShippingMethod required" check (live_rate has
  // `meta.shippingMethods === []`, so `selected` is null and the gate would
  // otherwise stay false forever → Pay button never enables → buyer cannot
  // pay even after the sub-flow completes address+rate collection). The
  // parcel* fields mirror `aggregateParcel`'s expectations downstream so the
  // sub-flow's rate fetch has identical inputs whether the buyer arrives via
  // cart or via the offer/auction settle surface.
  //
  // Field-mapping decision: CartItemData uses flat camelCase
  // (`parcelWeight`/`parcelLength`/...), CheckoutMeta carries the nested
  // ShippingRatesParcel (`weight`/`length`/.../`massUnit`/`distanceUnit`).
  // We unbundle here so the gate + sub-flow code paths read the same shape
  // they read for cart items.
  const liveRateFields = {
    shippingMode: meta.shippingMode,
    parcelWeight: meta.parcel?.weight,
    parcelLength: meta.parcel?.length,
    parcelWidth: meta.parcel?.width,
    parcelHeight: meta.parcel?.height,
    parcelMassUnit: meta.parcel?.massUnit,
    parcelDistanceUnit: meta.parcel?.distanceUnit,
  }

  if (variant.type === "offer") {
    return {
      id: variant.item.id,
      listingId: variant.item.listingId,
      sellerId: variant.sellerId,
      sellerName: variant.item.sellerName,
      sellerAvatarUrl: variant.item.sellerAvatarUrl,
      name: variant.item.title,
      description: "",
      price: variant.item.amount,
      currency: variant.currency,
      quantity: 1,
      maxQuantity: 1,
      images: variant.item.images,
      condition: "",
      grade: "",
      shippingMethods: meta.shippingMethods,
      selectedShippingMethod: selected,
      updated_at: new Date().toISOString(),
      paypalMerchantId: meta.paypalMerchantId,
      paypalCapabilities: meta.paypalCapabilities,
      availableProviders: meta.availableProviders,
      sellerCheckoutNote: meta.sellerCheckoutNote,
      ...liveRateFields,
    }
  }

  // type === "auction" — exhaustiveness probe below catches a new variant.
  if (variant.type === "auction") {
    return {
      id: variant.item.id,
      listingId: variant.listingId,
      sellerId: variant.sellerId,
      sellerName: variant.item.sellerName,
      sellerAvatarUrl: variant.item.sellerAvatarUrl,
      name: variant.item.title,
      description: variant.item.description,
      price: variant.item.winningAmount,
      currency: variant.currency,
      quantity: 1,
      maxQuantity: 1,
      images: variant.item.images,
      condition: variant.item.condition,
      grade: variant.item.grade,
      shippingMethods: meta.shippingMethods,
      selectedShippingMethod: selected,
      updated_at: new Date().toISOString(),
      paypalMerchantId: meta.paypalMerchantId,
      paypalCapabilities: meta.paypalCapabilities,
      availableProviders: meta.availableProviders,
      sellerCheckoutNote: meta.sellerCheckoutNote,
      ...liveRateFields,
    }
  }

  // Exhaustiveness probe — extending `PaymentSheetVariant` without handling
  // it here will fail the build with a `Type 'X' is not assignable to never`.
  const _exhaustive: never = variant
  throw new Error(`payment-sheet: unreachable variant ${JSON.stringify(_exhaustive)}`)
}

// One SoT for the variant→discriminator mapping shared by both
// `buildCheckoutPayload`'s `OrderCreationContext` (server price-source) and
// the per-item `intentItemDispatch` map (CheckoutIntent dispatch tier). Both
// previously had byte-identical bodies under different names — split would
// silently drift the next time one was edited. If the two surfaces ever need
// to diverge, add a parameter here rather than fork.
function buildOrderContextForVariant(variant: PaymentSheetVariant) {
  if (variant.type === "offer") return { kind: "offer" as const, offerId: variant.offerId }
  if (variant.type === "auction") return { kind: "auction" as const }
  const _exhaustive: never = variant
  throw new Error(`payment-sheet: unreachable variant ${JSON.stringify(_exhaustive)}`)
}

export function PaymentSheet({
  open,
  onOpenChange,
  variant,
  gate,
  title,
  onSettle,
  onClose,
  initialPendingOrderId = null,
  retryCopy: retryCopyProp,
  merchantCopy: merchantCopyProp,
  shippingLabel = "Shipping method",
}: PaymentSheetProps) {
  const isMobile = useMobile()
  const retryCopy = retryCopyProp ?? DEFAULT_RETRY_COPY
  const merchantCopy = merchantCopyProp ?? DEFAULT_MERCHANT_COPY

  // Shell-owned state: meta fetch, shipping selection, processing flags,
  // settle retry UI. Per-area gates and lifecycle are wrapper-owned.
  const [meta, setMeta] = useState<CheckoutMeta | null>(null)
  const [isMetaLoading, setIsMetaLoading] = useState(false)
  const [selectedShippingId, setSelectedShippingId] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(initialPendingOrderId)
  const [isRetryingSettle, setIsRetryingSettle] = useState(false)

  const listingIdForMeta = variant.type === "offer" ? variant.item.listingId : variant.listingId
  const userId: string | null = variant.userId
  const currency = variant.currency

  // Sync the wrapper-supplied initialPendingOrderId on open. The wrapper sets
  // it from server-truth pre-checks (auction /current-bid existingOrderId);
  // when the user reopens the sheet we re-honour that signal.
  useEffect(() => {
    if (!open) return
    setPendingOrderId(initialPendingOrderId ?? null)
  }, [open, initialPendingOrderId])

  // Fetch meta only once gate is `ready` — `blocked` paths render the
  // wrapper-supplied body without hitting the API, matching the previous
  // behaviour (offer wrapper short-circuits on deadlineExpired / not-buyer).
  useEffect(() => {
    if (!open) return
    if (gate.kind !== "ready") {
      setMeta(null)
      setSelectedShippingId(null)
      return
    }
    let cancelled = false
    setIsMetaLoading(true)
    fetchListingMeta(listingIdForMeta).then((next) => {
      if (cancelled) return
      const safe: CheckoutMeta = next ?? {
        shippingMethods: [],
        paypalMerchantId: null,
        sellerCheckoutNote: null,
        paypalCapabilities: [],
        availableProviders: [],
        shippingMode: null,
        parcel: null,
        currency: currency,
      }
      setMeta(safe)
      if (safe.shippingMethods.length > 0) {
        setSelectedShippingId(safe.shippingMethods[0].id)
      }
      setIsMetaLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [open, gate.kind, listingIdForMeta])

  const scopedItems = useMemo<CartItemData[]>(() => {
    if (!meta) return []
    return [variantToCartItem(variant, meta, selectedShippingId)]
  }, [meta, variant, selectedShippingId])

  // Live-rate buyer-side preview is owned by CheckoutForm — it overrides the
  // displayed shipping / service-fee / total inline via the `renderPaymentAction`
  // ctx callback (single SoT). Payment-sheet stays unaware.
  const { subtotal, shippingTotal, serviceFee, total, allShippingMethodsSelected, hasUnavailableItems } =
    useCartCalculations(scopedItems)

  const sellerDetails = useMemo<SellerDetail[]>(() => {
    if (scopedItems.length === 0) return []
    return [buildSellerDetail(scopedItems[0])]
  }, [scopedItems])

  // `buildCheckoutPayload` is the canonical builder — DO NOT fork. The
  // discriminator carries through to `OrderCreationContext` so the server
  // picks the right price source (offers.amount vs auction_highest_bid).
  const buildPayload = useCallback(
    (
      data: ShippingFormData,
      externalShipping?: CheckoutPayloadExternalShipping | null,
    ) =>
      buildCheckoutPayload(
        userId,
        scopedItems,
        data,
        currency,
        { subtotal, shippingTotal, serviceFee, total },
        buildOrderContextForVariant(variant),
        externalShipping,
      ),
    [userId, currency, scopedItems, subtotal, shippingTotal, serviceFee, total, variant],
  )

  const isMerchantBlocked = meta?.availableProviders.length === 0
  const isBusy = isProcessing || isRetryingSettle
  const disabled =
    isMetaLoading ||
    isBusy ||
    scopedItems.length === 0 ||
    hasUnavailableItems ||
    !allShippingMethodsSelected ||
    isMerchantBlocked

  const handleClose = useCallback(() => {
    onOpenChange(false)
    onClose?.()
  }, [onOpenChange, onClose])

  const handleRetrySettle = useCallback(async () => {
    if (!pendingOrderId) return
    setIsRetryingSettle(true)
    const settled = await onSettle(pendingOrderId)
    setIsRetryingSettle(false)
    if (settled) {
      setPendingOrderId(null)
      handleClose()
    }
  }, [pendingOrderId, onSettle, handleClose])

  const isCountryBannedForShipping = useCallback(
    (countryCode: string): boolean => {
      if (!countryCode || scopedItems.length === 0) return false
      const item = scopedItems[0]
      if (!item.selectedShippingMethod) {
        return (
          item.shippingMethods.length > 0 &&
          item.shippingMethods.every((m) => isPayPalCountryBanned(countryCode, m.bannedCountries))
        )
      }
      return isPayPalCountryBanned(countryCode, item.selectedShippingMethod.bannedCountries)
    },
    [scopedItems],
  )

  const content = (() => {
    // 1. Wrapper-blocked gate has top priority — deadline expired / not buyer
    //    / not winner / already settled. Wrapper supplies the body verbatim.
    if (gate.kind === "blocked") return gate.body
    if (gate.kind === "loading") {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )
    }

    // 2. Retry-settle UI takes priority over the checkout form. Reached when
    //    `initialPendingOrderId` is set (open-time existing-order pre-check)
    //    or when `onSettle` returned false after a successful PayPal capture.
    if (pendingOrderId) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center px-4">
          <AlertTriangle className="h-10 w-10 text-yellow-500 mb-4" />
          <p className="text-lg font-semibold">{retryCopy.title}</p>
          <p className="text-sm text-muted-foreground mt-2">{retryCopy.description}</p>
          <p className="text-xs text-muted-foreground/70 mt-3 font-mono select-all">
            Order ID: {pendingOrderId}
          </p>
          <Button
            className="mt-6 w-full max-w-xs"
            onClick={handleRetrySettle}
            disabled={isRetryingSettle}
          >
            {isRetryingSettle ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {retryCopy.retryingLabel}
              </>
            ) : (
              retryCopy.retryLabel
            )}
          </Button>
          <Button variant="ghost" className="mt-2" onClick={handleClose} disabled={isRetryingSettle}>
            {retryCopy.closeLabel}
          </Button>
        </div>
      )
    }

    // 3. Meta loading spinner.
    if (isMetaLoading || !meta) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )
    }

    // 4. Merchant-not-ready (server returned 0 rails).
    if (isMerchantBlocked) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center px-4">
          <AlertTriangle className="h-10 w-10 text-yellow-500 mb-4" />
          <p className="text-lg font-semibold">{merchantCopy.title}</p>
          <p className="text-sm text-muted-foreground mt-2">{merchantCopy.description}</p>
          {merchantCopy.detail ? (
            <p className="text-xs text-muted-foreground/70 mt-2">{merchantCopy.detail}</p>
          ) : null}
          <Button variant="ghost" className="mt-4" onClick={handleClose}>
            {merchantCopy.closeLabel}
          </Button>
        </div>
      )
    }

    // 5. Checkout form + PayPal/Stripe buttons (payment block stays inline —
    //    do NOT pass paymentPortalTarget; see file-level comment).
    return (
      <div>
        {meta.shippingMethods.length > 1 ? (
          <div className="px-4 pb-2">
            <label className="text-sm font-medium mb-2 block">{shippingLabel}</label>
            <Select
              surface="dialog"
              value={selectedShippingId ?? ""}
              onValueChange={setSelectedShippingId}
            >
              <SelectTrigger className="h-12 rounded-3xl">
                <SelectValue placeholder={shippingLabel} />
              </SelectTrigger>
              <SelectContent>
                {meta.shippingMethods.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} — {formatPrice(m.price, currency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <CheckoutForm
          className={isMobile ? "bg-sheet text-foreground" : ""}
          embedded
          sellerCheckoutNote={meta.sellerCheckoutNote}
          subtotal={subtotal}
          shippingTotal={shippingTotal}
          serviceFee={serviceFee}
          total={total}
          currencyCode={currency}
          onCompleteAction={undefined}
          userId={userId}
          profile={{ id: userId ?? undefined }}
          renderPaymentAction={(formData, selectedCountry, ctx) => (
            <PayPalCheckout
              intent={
                formData && scopedItems[0]?.sellerId
                  ? buildCheckoutIntent(
                      scopedItems,
                      formData,
                      currency,
                      Object.fromEntries(
                        scopedItems.map((i) => [i.id, buildOrderContextForVariant(variant)]),
                      ),
                      scopedItems[0].sellerId,
                    )
                  : null
              }
              totalAmount={ctx.effectiveTotal}
              sellerDetails={sellerDetails}
              formData={formData}
              selectedCountry={selectedCountry}
              currencyCode={currency}
              shippingMode={meta.shippingMode ?? undefined}
              parcel={meta.parcel ?? undefined}
              onLiveRateAmountChange={ctx.onLiveRateAmountChange}
              disabled={disabled || !isShippingFormComplete(formData) || (formData ? isCountryBannedForShipping(formData.country) : false)}
              isCountryBannedForShipping={isCountryBannedForShipping}
              getCheckoutPayload={(extras) => {
                if (!formData || !userId) return null
                return buildPayload(formData, extras?.externalShipping ?? null)
              }}
              onApproveAction={async (paypalData) => {
                if (!formData) return
                setIsProcessing(true)
                try {
                  if (paypalData.orderId) {
                    // Server may have pre-settled in the capture route. The
                    // discriminator-specific flag (`offerSettled` /
                    // `auctionSettled`) skips the client-side settle in that
                    // case — same idempotency contract as before.
                    const preSettled =
                      variant.type === "offer"
                        ? paypalData.offerSettled
                        : paypalData.auctionSettled
                    if (!preSettled) {
                      const settled = await onSettle(paypalData.orderId)
                      if (!settled) {
                        setPendingOrderId(paypalData.orderId)
                        return
                      }
                    }
                  }
                  handleClose()
                } catch (err) {
                  console.error("[payment-sheet] post-capture flow failed", err)
                  handleClose()
                } finally {
                  setIsProcessing(false)
                }
              }}
            />
          )}
        />
      </div>
    )
  })()

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) handleClose()
      else onOpenChange(next)
    },
    [handleClose, onOpenChange],
  )

  if (isMobile) {
    return (
      <DockSheet open={open} onOpenChange={handleOpenChange} title={title} zIndex={SHEET_Z_NESTED}>
        {content}
      </DockSheet>
    )
  }

  /* Desktop used to paint a close-only row while the sheet showed `title` —
     the mobile/desktop mismatch called out in HANDOFF_DIALOG_CHROME §8
     finding 2. There is no hero heading in the body to collide with (the body
     is the checkout form / spinner / retry view), so the desktop title was an
     omission rather than a design, and the twins are aligned on `standard`.

     The old X was `disabled={isBusy}`; that lives in the primitive now
     (`closeDisabled`), restored 1:1 — including the fact that it covers only
     the button, not Escape/backdrop, which were unguarded here before too. */
  return (
    <DialogCard
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      closeDisabled={isBusy}
      panelClassName="max-w-2xl h-[80vh]"
    >
      {content}
    </DialogCard>
  )
}
